// Popular currencies. decimals: 0 = whole units (IDR/JPY/KRW/VND), 2 = cents.
import { describePayment } from "./payments";

export const CURRENCIES = {
  IDR: { code: "IDR", symbol: "Rp", decimals: 0, locale: "id-ID", name: "Indonesian Rupiah", flag: "🇮🇩" },
  USD: { code: "USD", symbol: "$", decimals: 2, locale: "en-US", name: "US Dollar", flag: "🇺🇸" },
  EUR: { code: "EUR", symbol: "€", decimals: 2, locale: "de-DE", name: "Euro", flag: "🇪🇺" },
  GBP: { code: "GBP", symbol: "£", decimals: 2, locale: "en-GB", name: "British Pound", flag: "🇬🇧" },
  AUD: { code: "AUD", symbol: "A$", decimals: 2, locale: "en-AU", name: "Australian Dollar", flag: "🇦🇺" },
  SGD: { code: "SGD", symbol: "S$", decimals: 2, locale: "en-SG", name: "Singapore Dollar", flag: "🇸🇬" },
  JPY: { code: "JPY", symbol: "¥", decimals: 0, locale: "ja-JP", name: "Japanese Yen", flag: "🇯🇵" },
  KRW: { code: "KRW", symbol: "₩", decimals: 0, locale: "ko-KR", name: "South Korean Won", flag: "🇰🇷" },
  VND: { code: "VND", symbol: "₫", decimals: 0, locale: "vi-VN", name: "Vietnamese Dong", flag: "🇻🇳" },
  THB: { code: "THB", symbol: "฿", decimals: 2, locale: "th-TH", name: "Thai Baht", flag: "🇹🇭" },
  CNY: { code: "CNY", symbol: "¥", decimals: 2, locale: "zh-CN", name: "Chinese Yuan", flag: "🇨🇳" },
  MYR: { code: "MYR", symbol: "RM", decimals: 2, locale: "ms-MY", name: "Malaysian Ringgit", flag: "🇲🇾" },
  PHP: { code: "PHP", symbol: "₱", decimals: 2, locale: "en-PH", name: "Philippine Peso", flag: "🇵🇭" },
};

export function currencyInfo(code) {
  return CURRENCIES[(code || "IDR").toUpperCase()] || CURRENCIES.IDR;
}

// Format an amount with the correct symbol and decimals for a currency.
export const money = (n, currency = "IDR") => {
  const c = currencyInfo(currency);
  const v = Number(n) || 0;
  const num = v.toLocaleString(c.locale, { minimumFractionDigits: c.decimals, maximumFractionDigits: c.decimals });
  return `${c.symbol}\u00A0${num}`;
};

// Grouped number WITHOUT the symbol, for money inputs.
export const moneyPlain = (n, currency = "IDR") => {
  const c = currencyInfo(currency);
  const v = Number(n) || 0;
  return v.toLocaleString(c.locale, { minimumFractionDigits: c.decimals, maximumFractionDigits: c.decimals });
};

export const idr = (n) => money(n, "IDR");

export const idrShort = (n) => moneyPlain(n, "IDR");

export function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// WCAG-ish contrast pick: white or near-black text on a given hex background.
export function contrastText(hex) {
  if (!hex) return "#ffffff";
  const c = hex.replace("#", "");
  const r = parseInt(c.substring(0, 2), 16) / 255;
  const g = parseInt(c.substring(2, 4), 16) / 255;
  const b = parseInt(c.substring(4, 6), 16) / 255;
  const lin = (x) => (x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4));
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return L > 0.45 ? "#0F172A" : "#ffffff";
}

// Group-aware initials that are guaranteed UNIQUE within a bill so two people
// with the same initials (e.g. Maudy / Maureen) don't both show "MA". Labels
// are reserved globally across ALL participants (a name's extended label can't
// collide with another person's base label either).
export function computeInitialsMap(people) {
  const list = (people || []).map((p) => ({ id: p.id, name: (p.name || "").trim() }));
  const base = (name) => {
    const t = name.split(/\s+/).filter(Boolean);
    if (t.length === 0) return "?";
    if (t.length === 1) return t[0].slice(0, 2).toUpperCase();
    return (t[0][0] + t[t.length - 1][0]).toUpperCase();
  };
  const candidates = (name) => {
    const arr = [base(name)];
    const first = (name[0] || "?").toUpperCase();
    for (let i = 1; i < name.length; i++) {
      const c = name[i];
      if (!c || /\s/.test(c)) continue;
      arr.push(first + c.toUpperCase());
    }
    for (let k = 1; k <= 99; k++) arr.push(first + k);
    return arr;
  };
  const used = new Set();
  const map = {};
  list.forEach((p) => {
    const cands = candidates(p.name);
    const label = cands.find((c) => !used.has(c)) || String(p.id).slice(0, 2).toUpperCase();
    used.add(label);
    map[p.id] = label;
  });
  return map;
}

export function firstName(name) {
  return (name || "").trim().split(/\s+/)[0] || "";
}

// --- Assignment helpers. Each item stores `portions`: a map of
// participant_id -> quantity (fractional allowed). "Tap" adds one whole unit
// (capped at qty); "Split evenly" divides the item equally (e.g. 3 nasi / 2 =
// 1.5 each). Legacy bills stored `assigned_to` as a multiset; we migrate on read.
const EPS = 1e-6;

export function itemUnits(item) {
  const n = Math.round(Number(item?.qty) || 0);
  return n > 0 ? n : 0;
}
export function itemPortions(item) {
  const p = item?.portions;
  if (p && typeof p === "object" && !Array.isArray(p)) {
    const out = {};
    Object.entries(p).forEach(([k, v]) => {
      const n = Number(v) || 0;
      if (n > EPS) out[k] = n;
    });
    return out;
  }
  const out = {}; // legacy multiset fallback
  (item?.assigned_to || []).forEach((a) => (out[a] = (out[a] || 0) + 1));
  return out;
}
export function portionFor(item, pid) {
  return itemPortions(item)[pid] || 0;
}
export function totalAssigned(item) {
  return Object.values(itemPortions(item)).reduce((a, b) => a + b, 0);
}
export function isFullyAssigned(item) {
  const qty = Number(item?.qty) || 0;
  if (qty <= 0) return true;
  return totalAssigned(item) >= qty - EPS;
}
// Effective quantity a person actually pays for. When more units are claimed
// than exist (shared), each claim is worth less than a whole unit — e.g. a 1x
// water tapped by 2 people shows 0.5 each.
export function effectiveUnits(item, pid) {
  const qty = Number(item?.qty) || 0;
  const portion = portionFor(item, pid);
  if (portion <= EPS) return 0;
  const denom = Math.max(qty, totalAssigned(item));
  if (denom <= 0) return 0;
  return (portion * qty) / denom;
}
// Human-friendly quantity: 2 -> "2", 1.5 -> "1.5", 0.33 -> "0.33".
export function fmtQty(n) {
  const v = Number(n) || 0;
  return Math.abs(v - Math.round(v)) < EPS ? String(Math.round(v)) : String(+v.toFixed(2));
}

// Per-person detailed breakdown whose displayed lines reconcile EXACTLY to the
// server-authoritative total. Item shares are rounded for display; a small
// per-person "rounding" line absorbs the largest-remainder difference so
// items - discount + service + tax + rounding === total.
export function personBreakdown(p, items, decimals = 0) {
  const scale = Math.pow(10, decimals);
  const qd = (v) => Math.round(v * scale) / scale;
  let itemsSubtotal = 0;
  const lines = [];
  (items || []).forEach((it) => {
    const qtyP = portionFor(it, p.id);
    if (qtyP <= EPS) return;
    const qtyTotal = Number(it.qty) || 0;
    const itemTotal = qtyTotal * (Number(it.unit_price) || 0);
    const s = totalAssigned(it);
    const denom = Math.max(qtyTotal, s);
    if (denom <= 0) return;
    const share = qd((qtyP * itemTotal) / denom);
    itemsSubtotal += share;
    const eff = (qtyP * qtyTotal) / denom; // effective units paid for
    const tag = Math.abs(eff - 1) > EPS ? ` \u00D7${fmtQty(eff)}` : qtyTotal > 1 ? ` (1 of ${itemUnits(it)})` : "";
    lines.push({ id: it.id, name: (it.name || "Item") + tag, amount: share });
  });
  const service = p.service || 0;
  const tax = p.tax || 0;
  const discount = p.discount || 0;
  const total = p.total || 0;
  const rounding = qd(total - (itemsSubtotal - discount + service + tax));
  return { lines, itemsSubtotal, discount, service, tax, rounding, total };
}

// Returns a compact payment string, or null if empty.

// Build a clean, paste-friendly text breakdown of the whole split.
export function buildBreakdownText({ title, participants, items, summary, url, payment }) {
  const cur = summary.currency || "IDR";
  const dec = summary.decimals ?? currencyInfo(cur).decimals;
  const m = (v) => money(v, cur);
  const lines = [];
  lines.push(`${title || "Bill"} · gigazip splitbill`);
  lines.push("--------------------------------");
  lines.push(`Subtotal   : ${m(summary.subtotal)}`);
  if (summary.discount > 0) lines.push(`Discount   : -${m(summary.discount)}`);
  if (summary.service_amount > 0) lines.push(`Service    : ${m(summary.service_amount)}`);
  if (summary.tax_amount > 0) lines.push(`Tax        : ${m(summary.tax_amount)}`);
  if (summary.rounding && summary.rounding !== 0)
    lines.push(`Rounding   : ${summary.rounding < 0 ? "-" : ""}${m(Math.abs(summary.rounding))}`);
  lines.push(`TOTAL      : ${m(summary.grand_total)}`);
  lines.push("--------------------------------");

  (summary.people || []).forEach((p, idx) => {
    if (idx > 0) lines.push(""); // blank line between each person block
    lines.push(`${p.name}: ${m(p.total)}`);
    const bd = personBreakdown(p, items || [], dec);
    bd.lines.forEach((l) => lines.push(`  • ${l.name}: ${m(l.amount)}`));
    const extra = [];
    if (bd.discount > 0) extra.push(`discount -${m(bd.discount)}`);
    if (bd.service > 0) extra.push(`service ${m(bd.service)}`);
    if (bd.tax > 0) extra.push(`tax ${m(bd.tax)}`);
    if (bd.rounding !== 0) extra.push(`rounding ${bd.rounding < 0 ? "-" : "+"}${m(Math.abs(bd.rounding))}`);
    if (extra.length) lines.push(`  (${extra.join(" · ")})`);
  });

  const pd = describePayment(payment);
  if (pd) {
    lines.push("");
    lines.push("--------------------------------");
    lines.push(`Everyone pays to (${pd.currency}):`);
    lines.push(`  ${pd.oneLine}`);
  }

  if (summary.has_unassigned) {
    lines.push("");
    lines.push(`Unassigned : ${m(summary.unassigned_amount)}`);
  }
  lines.push(url ? `View: ${url}` : "Split with gigazip");
  return lines.join("\n");
}

export function whatsappUrl(text) {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

// Export a DOM node as a PNG that works on BOTH desktop and mobile.
// Mobile browsers (esp. iOS Safari) ignore the <a download> trick, so we use
// the native share sheet (Save to Photos / share) when files can be shared,
// and fall back to a Blob download + open-in-tab elsewhere.
export async function exportNodeAsImage(node, filename) {
  if (!node) throw new Error("nothing to export");
  const { toBlob } = await import("html-to-image");
  const blob = await toBlob(node, { pixelRatio: 2, backgroundColor: "#ffffff", cacheBust: true });
  if (!blob) throw new Error("render failed");

  const file = new File([blob], filename, { type: "image/png" });

  if (typeof navigator !== "undefined" && navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: filename.replace(/\.png$/i, "") });
      return "shared";
    } catch (e) {
      if (e && (e.name === "AbortError" || e.name === "NotAllowedError")) {
        // user cancelled or gesture lost — fall through to download/open
      }
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Mobile browsers that ignore `download`: open the image so the user can
  // long-press → "Save image".
  const isMobile = typeof navigator !== "undefined" && /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent || "");
  if (isMobile) window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 15000);
  return "downloaded";
}

// Warm the html-to-image module so the export click has no async import delay
// (important for keeping the iOS share gesture valid).
export function warmImageExport() {
  import("html-to-image").catch(() => {});
}

// Resize an image file on the client before upload (max edge px, jpeg quality).
export function resizeImage(file, maxEdge = 1600, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => (img.src = e.target.result);
    reader.onerror = reject;
    img.onload = () => {
      let { width, height } = img;
      const scale = Math.min(1, maxEdge / Math.max(width, height));
      width = Math.round(width * scale);
      height = Math.round(height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error("resize failed"));
          resolve(new File([blob], "receipt.jpg", { type: "image/jpeg" }));
        },
        "image/jpeg",
        quality
      );
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}
